import { useEffect, useRef, useState } from 'react';
import { img, readdy } from '@/utils/images';
import ContactModal from '@/components/feature/ContactModal';

function CountUp({ end, duration = 2000 }: { end: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
          obs.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let startTime: number;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, end, duration]);

  return <span ref={ref}>{count}</span>;
}

export default function HeroSection() {
  const [scrollY, setScrollY] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'consultation' | 'franchise'>('consultation');

  useEffect(() => {
    setLoaded(true);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openModal = (type: 'consultation' | 'franchise') => {
    setModalType(type);
    setModalOpen(true);
  };

  const parallaxY = scrollY * 0.35;
  const parallaxYSlow = scrollY * 0.15;

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      <ContactModal open={modalOpen} onClose={() => setModalOpen(false)} type={modalType} />
      <div
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{
          backgroundImage: `url(${readdy('hero-bg-lobby-v5-ref')})`,
          transform: `translateY(${parallaxY}px)`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/20 to-black/55" />

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute w-[500px] h-[500px] rounded-full blur-[150px] transition-all duration-[2000ms] ease-in-out"
          style={{
            background: 'radial-gradient(circle, rgba(210,180,140,0.22) 0%, transparent 70%)',
            top: `${20 + parallaxYSlow * 0.3}%`,
            left: `${30 + parallaxYSlow * 0.2}%`,
            transform: `translateY(${parallaxYSlow * 0.5}px)`,
          }}
        />
        <div
          className="absolute w-[350px] h-[350px] rounded-full blur-[120px] transition-all duration-[2000ms] ease-in-out"
          style={{
            background: 'radial-gradient(circle, rgba(245,235,220,0.18) 0%, transparent 70%)',
            bottom: `${30 - parallaxYSlow * 0.2}%`,
            right: `${20 - parallaxYSlow * 0.15}%`,
            transform: `translateY(-${parallaxYSlow * 0.4}px)`,
          }}
        />
        <div
          className="absolute w-[200px] h-[200px] rounded-full blur-[100px]"
          style={{
            background: 'radial-gradient(circle, rgba(188,143,100,0.12) 0%, transparent 70%)',
            top: `${55 + parallaxYSlow * 0.1}%`,
            left: `${55 + parallaxYSlow * 0.1}%`,
          }}
        />
      </div>

      <div className="absolute inset-0 pointer-events-none">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${4 + (i % 3) * 3}px`,
              height: `${4 + (i % 3) * 3}px`,
              background: i % 3 === 0 ? 'rgba(210,180,140,0.45)' : 'rgba(255,255,255,0.25)',
              top: `${15 + i * 13 + parallaxYSlow * 0.1 * (i % 2 === 0 ? 1 : -1)}%`,
              left: `${8 + i * 14 + parallaxYSlow * 0.08 * (i % 3 === 0 ? 1 : -1)}%`,
              animation: `float ${4 + i * 0.7}s ease-in-out infinite`,
              animationDelay: `${i * 0.4}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 w-full px-6 md:px-10 pt-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center min-h-[80vh]">
            <div className="lg:col-span-12">
              <div
                className={`mb-6 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 transition-all duration-700 ${
                  loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" />
                <span className="text-white/80 text-xs md:text-sm tracking-wider font-light">
                  麦杰集团 · 专业纤体连锁品牌
                </span>
              </div>

              <h1
                className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.06] mb-5"
                style={{ transition: 'all 0.8s cubic-bezier(0.22, 0.61, 0.36, 1)', opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(20px)', transitionDelay: '0.15s' }}
              >
                <span className="block">长期专注纤体瘦身领域</span>
                <span className="block">实力领先的连锁品牌</span>
              </h1>

              <p
                className="text-white/50 text-base md:text-lg leading-relaxed mb-10 max-w-lg font-light tracking-wide"
                style={{ transition: 'all 0.8s cubic-bezier(0.22, 0.61, 0.36, 1)', opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '0.3s' }}
              >
                十余年行业积淀，融合中医经络理论与现代健康管理，
                为每一位顾客提供个性化、科学、温和的形体管理方案。
              </p>

              <div
                className="flex flex-col sm:flex-row gap-4"
                style={{ transition: 'all 0.8s cubic-bezier(0.22, 0.61, 0.36, 1)', opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '0.45s' }}
              >
                <button
                  onClick={() => openModal('consultation')}
                  className="group relative px-8 py-4 bg-brand-deep text-white text-sm md:text-base font-medium rounded-full text-center whitespace-nowrap transition-all duration-300 cursor-pointer overflow-hidden"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-deep to-brand-dark opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    预约专属顾问
                    <i className="ri-arrow-right-up-line group-hover:translate-x-0.5 group-hover:-translate-y-0.5 inline-block transition-transform" />
                  </span>
                </button>
                <button
                  onClick={() => openModal('franchise')}
                  className="group px-8 py-4 bg-white/8 backdrop-blur-sm text-white text-sm md:text-base font-medium rounded-full text-center whitespace-nowrap border border-white/20 hover:bg-white/15 hover:border-white/35 transition-all duration-300 cursor-pointer"
                >
                  申请加盟
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 inline-block transition-transform" />
                </button>
              </div>

              <div
                className="flex items-center gap-6 mt-12"
                style={{ transition: 'all 0.8s cubic-bezier(0.22, 0.61, 0.36, 1)', opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(8px)', transitionDelay: '0.6s' }}
              >
                <div className="flex -space-x-2.5">
                  {[
                    img('readdy/4809c11142038d2b19ecef495d7d90f6.png'),
                    img('readdy/167da3301b1b1de99e08ec75923f5d6b.png'),
                    img('readdy/5df8bbf68c1acc78bacde88c2c6b63c3.png'),
                  ].map((src, i) => (
                    <div key={i} className="w-10 h-10 md:w-11 md:h-11 rounded-full border-2 border-white/25 overflow-hidden ring-2 ring-black/20">
                      <img
                        src={src}
                        alt="满意顾客"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-white text-base md:text-lg font-bold tabular-nums">
                    38万<span className="text-brand-gold">+</span>
                  </p>
                  <p className="text-white/35 text-xs tracking-wide">累计服务顾客</p>
                </div>
                <div className="w-px h-10 bg-white/10" />
                <div>
                  <p className="text-white text-base md:text-lg font-bold tabular-nums">
                    <CountUp end={200} />+
                  </p>
                  <p className="text-white/35 text-xs tracking-wide">品牌合作门店</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/25 text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-white/35 text-lg" />
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); opacity: 0.4; }
          50% { transform: translateY(-12px) scale(1.3); opacity: 0.8; }
        }
      `}</style>
    </section>
  );
}